import { type FC, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Box, Divider, Flex } from "@mantine/core";

import { useFeatureGates } from "../../cof-feature-gates/hooks";
import { type AbsoluteRoute, type RouteParams } from "../../core/routes";
import { useQueryParams, useV3Routes } from "../../core/routes/hooks";
import { RouteEntities } from "../../core/routes/relativeRoutes";
import { CofConfigId } from "../../gql-generated/types";
import { PageHeader } from "../../layout/components";
import { type LensInfo } from "../../lens/api/queries";
import { type LensesOfSelectedContainerResult } from "../../lenses/hooks";
import { FolderSelectionDropdown } from "../../shared/components/folder-selection";
import { LensSearch } from "../../shared/components/lens-search";
import { LinkStyled } from "../../shared/styles";
import { type TimeRangeChangeParams } from "../hooks";
import { LensGranularity, type LensInsightsTab } from "../models";
import { buildNextInsightsUrl, isLensInsightsTab } from "../utils/insightsNavigation";

import { LensDetailsButtonV3 } from "./LensDetailsButtonV3";
import { AllLensesLinkWrapper, dropdownTruncate, popoverClassName } from "./LensInsightsHeader.styles";
import {
    AllLensButtonText,
    InsightsPageTitle,
    TabNameAudience,
    TabNameConversion,
    TabNameCreatorPayoutMonetization,
    TabNameEngagement,
    TabNameEventInsights,
    TabNameFunnelAnalysis,
} from "./messages.generated";
import { SelectTimeRange } from "./select-time-range";
import { dropdownButtonContainer } from "./shared";

const tabNameComponents: Record<LensInsightsTab, FC> = {
    [RouteEntities.Audience]: TabNameAudience,
    [RouteEntities.Engagement]: TabNameEngagement,
    [RouteEntities.EventInsights]: TabNameEventInsights,
    [RouteEntities.Conversion]: TabNameConversion,
    [RouteEntities.Funnel]: TabNameFunnelAnalysis,
    [RouteEntities.Monetization]: TabNameCreatorPayoutMonetization,
};

type LensInsightsHeaderV3Props = {
    selectedLens?: LensInfo;
    hideAllLensesLink: boolean;
    hideLensSearch?: boolean;
    showAggregatedInsights?: boolean;
    hideFolderSelectionDropdown?: boolean;
} & LensesOfSelectedContainerResult;

export const LensInsightsHeader: FC<LensInsightsHeaderV3Props> = ({
    selectedLens,
    hideAllLensesLink,
    hideLensSearch,
    hideFolderSelectionDropdown,
    showAggregatedInsights,
    lensSearchText,
    setLensSearchText,
    ...lensesOfSelectedContainerResult
}) => {
    const { goToSelectedOrgLensesInsights } = useV3Routes();
    const navigate = useNavigate();
    const { updateQueryParams } = useQueryParams();
    const { insightsTab, orgId, lensFolderId } = useParams<RouteParams[AbsoluteRoute.ORG_LENS_FOLDER_INSIGHTS_TAB]>();
    const { configs } = useFeatureGates();
    const isMonetizationHubEnabled = configs[CofConfigId.MyLensesMonetizationInsights].enabled;

    const resolvedTab: LensInsightsTab | undefined = isLensInsightsTab(insightsTab) ? insightsTab : undefined;
    const TabName = resolvedTab ? tabNameComponents[resolvedTab] : null;

    const { ...lensesOfSelectedContainer } = lensesOfSelectedContainerResult;
    const handleSetSelectedLens = useCallback(
        (lens: LensesOfSelectedContainerResult["lenses"][number] | null) => {
            if (lens) {
                updateQueryParams({ lensId: lens.id });
            } else {
                goToSelectedOrgLensesInsights();
            }
        },
        [updateQueryParams, goToSelectedOrgLensesInsights],
    );

    const handleTimeRangeChange = useCallback(
        ({ selectedTimeRange, searchParams }: TimeRangeChangeParams) => {
            navigate(
                buildNextInsightsUrl({
                    orgId,
                    lensFolderId,
                    currentTab: insightsTab,
                    currentSearch: searchParams,
                    nextTimeRange: selectedTimeRange,
                    selectedLens,
                    isMonetizationHubEnabled,
                }),
            );
        },
        [insightsTab, isMonetizationHubEnabled, lensFolderId, navigate, orgId, selectedLens],
    );

    return (
        <Flex
            align={{ base: "flex-start", xs: "center" }}
            justify={{ base: "flex-start", xs: "space-between" }}
            direction={{ base: "column", xs: "row" }}
            gap="sm"
            wrap="wrap"
            className={dropdownButtonContainer}
            pb="xs"
        >
            <PageHeader>
                <Flex align="center" gap="sm">
                    <InsightsPageTitle />
                    {TabName && (
                        <>
                            <Divider orientation="vertical" size="xs" />
                            <TabName />
                        </>
                    )}
                </Flex>
            </PageHeader>

            <Flex align="center" gap="sm" wrap="nowrap" w={{ base: "100%", xs: "auto" }}>
                {!hideFolderSelectionDropdown && (
                    <Box style={{ flex: 1, minWidth: 0 }} className={dropdownTruncate}>
                        <FolderSelectionDropdown
                            headerChildren={
                                showAggregatedInsights && (
                                    <AllLensesLinkWrapper>
                                        <LinkStyled to={"/insights"}>
                                            <AllLensButtonText />
                                        </LinkStyled>
                                    </AllLensesLinkWrapper>
                                )
                            }
                            fallbackText={showAggregatedInsights ? <AllLensButtonText /> : ""}
                        />
                    </Box>
                )}
                {!hideLensSearch && (
                    <Box style={{ flex: 1, minWidth: 0 }} className={dropdownTruncate}>
                        <LensSearch
                            selectedLens={selectedLens ?? null}
                            setSelectedLens={handleSetSelectedLens}
                            hideAllLensesLink={hideAllLensesLink}
                            popoverClassName={popoverClassName}
                            searchText={lensSearchText}
                            setSearchText={setLensSearchText}
                            lensFolderId={lensFolderId}
                            {...lensesOfSelectedContainer}
                        />
                    </Box>
                )}
            </Flex>

            <Flex align="center" gap="sm" wrap="nowrap">
                <LensDetailsButtonV3 />
                <SelectTimeRange
                    onTimeRangeChange={handleTimeRangeChange}
                    defaultRange={
                        resolvedTab === RouteEntities.Monetization ? LensGranularity.Lifetime : LensGranularity.Month
                    }
                />
            </Flex>
        </Flex>
    );
};
